import { useEffect } from 'react';

import { Box, ListItem, Text, UnorderedList } from '@chakra-ui/react';

import { SummaryFrame } from '@inspire/ui/chakra/layout/Frame';

import { CheckboxGame, RadioGame } from 'components/activites/iterative-games';

const SummarySection = ({ questions, options, questionIndex }) => {
  const optionsWithAnswers = options.map((o) => ({
    ...o,
    answeredQuestions: questions
      .slice(0, questionIndex)
      .filter((q) => q.answerValue === o.value),
  }));

  const extraInfos = questions
    .slice(0, questionIndex)
    .filter((q) => q.extraInfo)
    .map((q) => q.extraInfo);

  return (
    <SummaryFrame mt={8} px={6}>
      {optionsWithAnswers.map((oa) => (
        <Text key={oa.value} my={2}>
          <b>{oa.fullName}</b> :{' '}
          {oa.answeredQuestions.map((q) => q.subject).join(', ')}
        </Text>
      ))}
      {extraInfos?.length > 0 &&
        extraInfos.map((info, i) => (
          <Text key={i}>
            <i>{info}</i>
          </Text>
        ))}
    </SummaryFrame>
  );
};

export const FormationsLieu = ({ allowNext }) => {
  const questions = [
    {
      subject: 'BTS',
      answerValues: ['lycee', 'cfa'],
    },
    { subject: 'CS', answerValues: ['lycee', 'cfa', 'prive'] },
    { subject: 'FCIL/SIL', answerValues: ['lycee', 'cfa'] },
    { subject: 'BUT', answerValues: ['universite'] },
    { subject: 'Licences générales', answerValues: ['universite'] },
    { subject: 'CPGE', answerValues: ['lycee'] },
  ];

  const options = [
    { shortName: 'Lycée', value: 'lycee' },
    { shortName: 'CFA', value: 'cfa' },
    { shortName: 'Université', value: 'universite' },
    { shortName: 'Ecole privée', value: 'prive' },
  ];

  useEffect(() => {
    allowNext(false);
  }, []);

  return (
    <CheckboxGame
      questions={questions}
      getOptions={() => options}
      onFinished={() => allowNext(true)}
      introSection={
        <Text textAlign={'center'}>
          Quels lieux d’étude correspondent aux formations suivantes ?
          (plusieurs réponses possibles).
        </Text>
      }
      getQuestionSection={(question) => (
        <Text>
          <i>
            Où sont enseignés les <b>{question.subject}</b> ?
          </i>
        </Text>
      )}
      getSummarySection={(_, questionIndex) =>
        questionIndex !== 0 && (
          <SummaryFrame mt={5}>
            <UnorderedList>
              {questions.slice(0, questionIndex).map((q) => (
                <ListItem key={q.subject}>
                  <b>{q.subject} :</b>{' '}
                  {q.answerValues
                    .map((av) => options.find((o) => o.value === av).shortName)
                    .join(', ')}
                </ListItem>
              ))}
            </UnorderedList>
          </SummaryFrame>
        )
      }
    />
  );
};

export const LaBonneDefinition = ({ allowNext }) => {
  const questions = [
    {
      text: 'Qu’est ce qu’une FCIL ?',
      answerValue: '2',
      options: [
        {
          shortName: 'Formation de Commerce International et Local',
          value: '1',
        },
        {
          shortName: 'Formation Complémentaire d’Initiative Locale',
          summary:
            'La formation Complémentaire d’Initiative Locale dure entre 6 et 18 mois, et alterne cours en établissement scolaire et expérience professionnelle en entreprise, elle permet d’obtenir une qualification en lien avec le marché local.',
          value: '2',
        },
        {
          shortName: 'Formation du Centre Institutionnel de Loisir',
          value: '3',
        },
      ],
    },
    {
      text: 'Qu’est ce qu’une SIL ?',
      answerValue: '1',
      options: [
        {
          shortName: 'Spécialisation d’Initiative Locale',
          summary:
            'La Spécialisation d’Initiative Locale est un complément de formation de courte durée, elle permet d’obtenir une qualification en lien avec le marché local dans le secteur agricole.',
          value: '1',
        },
        {
          shortName: 'Spécificité Industrielle Locale',
          value: '2',
        },
        {
          shortName: 'Spécialité Initiale Locale',
          value: '3',
        },
      ],
    },
    {
      text: 'Qu’est ce qu’un BTS ?',
      answerValue: '3',
      options: [
        {
          shortName: 'Brevet de Technique Suplémentaire',
          value: '1',
        },
        {
          shortName: 'Bachelor de Technicien Supérieur',
          value: '2',
        },
        {
          shortName: 'Brevet de Technicien Supérieur',
          summary:
            "Le Brevet de Technicien Supérieur est une formation de 2 ans qui permet d'obtenir un diplôme professionnalisé. Il existe des BTS dans de nombreux secteurs, notamment les BTSA dans le secteur agricole.",
          value: '3',
        },
      ],
    },
    {
      text: 'Qu’est ce qu’un CS ?',
      answerValue: '1',
      options: [
        {
          shortName: 'Certificat de Spécialisation',
          summary:
            'Le Certificat de Spécialisation est une formation de courte durée permettant d’obtenir un diplôme afin d’exercer une activité professionnelle spécialisée dans différents domaines.',
          value: '1',
        },
        {
          shortName: 'Classe Spécifique',
          value: '2',
        },
        {
          shortName: 'Centre Spécialisé',
          value: '3',
        },
      ],
    },
    {
      text: 'Qu’est ce qu’un BUT ?',
      answerValue: '1',
      options: [
        {
          shortName: 'Bachelor Universitaire de Technologie',
          summary:
            'le Bachelor Universitaire de Technologie s’obtient en 3 ans dans un IUT (Institut Universitaire de Technologie) dans une université.',
          value: '1',
        },
        {
          shortName: 'Brevet Universitaire Touristique',
          value: '2',
        },
        {
          shortName: 'Bachelor Universitaire Territorial',
          value: '3',
        },
      ],
    },
    {
      text: 'Qu’est ce qu’une CPGE ?',
      answerValue: '2',
      options: [
        {
          shortName: 'Centre Périphérique de Groupe Énergétique',
          value: '1',
        },
        {
          shortName: 'Classe Préparatoire aux Grandes Écoles',
          summary:
            'La Classe Préparatoire aux Grandes Écoles ou “classe prépa”, permet de préparer les concours d’entrée en écoles (écoles normales supérieures, écoles vétérinaires, militaires, d’ingénieurs et de commerce, etc.).',
          value: '2',
        },
        {
          shortName: 'Classe Préparatoire aux Grandes Économies',
          value: '3',
        },
      ],
    },
    {
      text: 'Qu’est qu’un CFA et un CFPPA ?',
      answerValue: '3',
      options: [
        {
          shortName: 'Des formations',
          value: '1',
        },
        {
          shortName: 'Des sites internet pour les études',
          value: '2',
        },
        {
          shortName: 'Des centres qui proposent des formations en alternance',
          summary: 'Des centres qui proposent des formations en alternance.',
          value: '3',
        },
      ],
      questionCFA: true,
    },
  ];

  useEffect(() => {
    allowNext(false);
  }, []);

  return (
    <RadioGame
      isVertical
      questions={questions}
      getOptions={(question) => question?.options}
      onFinished={() => allowNext(true)}
      introSection={
        <Text textAlign={'center'}>
          Sélectionne la bonne définition des acronymes de formations
        </Text>
      }
      getQuestionSection={(question) => (
        <Text mt={0}>
          <i>{question.text}</i>
        </Text>
      )}
      getSummarySection={(_, questionIndex) =>
        questionIndex !== 0 && (
          <SummaryFrame mt={8}>
            <UnorderedList>
              {questions.slice(0, questionIndex).map((q) => (
                <>
                  <ListItem key={q.text}>
                    <b>{q.text} :</b>{' '}
                    {q.options.find((c) => c.value === q.answerValue).summary}
                  </ListItem>
                  {q.questionCFA && (
                    <div style={{ paddingLeft: '16px' }}>
                      <ListItem>
                        <b>CFA :</b> Centre de formation d'apprentis.
                      </ListItem>

                      <ListItem>
                        <b>CFPPA :</b> Centre de formation professionnelle et de
                        promotion agricole (propose des formations en
                        apprentissage spécialisées dans le domaine agricole).
                      </ListItem>
                    </div>
                  )}
                </>
              ))}
            </UnorderedList>
          </SummaryFrame>
        )
      }
    />
  );
};

export const DureeFormations = ({ allowNext }) => {
  const questions = [
    {
      subject: 'CS, FCIL/SIL',
      answerValue: '1',
      isPlural: true,
    },
    {
      subject: `BTS et classes préparatoires`,
      answerValue: '2',
      isPlural: true,
    },
    { subject: 'licences générales', answerValue: '3', isPlural: true },
  ];

  const options = [
    {
      fullName: "Formations d'1 an ou moins",
      shortName: 'Environ 1 an',
      value: '1',
    },
    {
      fullName: 'Formations de 2 ans',
      shortName: '2 ans',
      value: '2',
    },
    {
      fullName: 'Formations de 3 ans',
      shortName: '3 ans',
      value: '3',
    },
  ];

  useEffect(() => {
    allowNext(false);
  }, []);

  return (
    <RadioGame
      questions={questions}
      getOptions={() => options}
      onFinished={() => allowNext(true)}
      introSection={
        <Text textAlign={'center'}>
          Dans cette activité, tu dois indiquer la <b>durée des formations</b>.
        </Text>
      }
      getQuestionSection={(question) => (
        <Text>
          <i>
            {question.subject === 'licences générales'
              ? 'Les '
              : question.isPlural
              ? 'Les formations '
              : 'La formation '}{' '}
            <b>{question?.subject}</b> {question.isPlural ? 'durent' : 'dure'}
            ...
          </i>
        </Text>
      )}
      getSummarySection={(_, questionIndex) =>
        questionIndex !== 0 && (
          <SummarySection
            questions={questions}
            options={options}
            questionIndex={questionIndex}
          />
        )
      }
    />
  );
};

export const FormatDEnseignement = ({ allowNext }) => {
  const questions = [
    {
      subject: 'CS, FCIL/SIL et BTS',
      answerValue: 'pratique',
      isPlural: true,
    },
    {
      subject: 'BUT',
      answerValue: 'les-deux',
      extraInfo:
        'Le BUT est une formation à la fois théorique et pratique, elle mêle des enseignements généraux, théoriques et professionnels avec des périodes de mise en pratique via des stages.',
    },
    { subject: 'Licence générale', answerValue: 'theorique' },
  ];

  const options = [
    {
      fullName: 'Formations pratiques',
      shortName: 'Pratique (avec période de formation en milieu professionnel)',
      value: 'pratique',
    },
    {
      fullName: 'Formations théoriques',
      shortName: 'Théorique',
      value: 'theorique',
    },
    {
      fullName: 'Formations pratiques et théoriques',
      shortName: 'Les deux',
      value: 'les-deux',
    },
  ];

  useEffect(() => {
    allowNext(false);
  }, []);

  return (
    <RadioGame
      questions={questions}
      getOptions={() => options}
      onFinished={() => allowNext(true)}
      isVertical
      introSection={
        <Text>
          Dans cette activité, tu dois dire si les formations sont{' '}
          <b>pratiques</b>, <b>théoriques</b>, ou bien <b>les deux</b>.
        </Text>
      }
      infoSection={
        <Box bg="gray.100" p={5} borderRadius={4} mb={8}>
          <UnorderedList my={0}>
            <ListItem>
              Une formation <b>pratique</b> prépare les étudiants de manière
              concrète à la pratique d’un métier via des périodes de stage plus
              longues et l’enseignement de matières professionnelles.
            </ListItem>
            <ListItem>
              Une formation <b>théorique</b> permet aux étudiants d’approfondir
              leurs connaissances via des enseignements généraux et théoriques.
            </ListItem>
            <ListItem>
              Une formation <b>pratique et théorique</b> mêle enseignants
              généraux, théoriques et professionnels avec des périodes de mise
              en pratique via des stages.
            </ListItem>
          </UnorderedList>
        </Box>
      }
      getQuestionSection={(question) => (
        <Text>
          <i>
            Dans {question.isPlural ? 'les formations ' : 'la formation '}{' '}
            <b>{question?.subject}</b> l’enseignement est plus ?
          </i>
        </Text>
      )}
      getSummarySection={(_, questionIndex) =>
        questionIndex !== 0 && (
          <SummarySection
            questions={questions}
            options={options}
            questionIndex={questionIndex}
          />
        )
      }
    />
  );
};

export const TypeDEnseignement = ({ allowNext }) => {
  const questions = [
    {
      subject:
        'Parmi les formations suivantes, quelles sont les deux formations où l’accompagnement par les enseignants est moins important que dans ta classe actuelle ?',
      answerValues: ['licence', 'BUT'],
    },
  ];

  const options = [
    {
      fullName: 'CS',
      shortName: 'CS',
      value: 'CS',
    },
    {
      fullName: 'FCIL',
      shortName: 'FCIL/SIL',
      value: 'FCIL',
    },
    {
      fullName: 'BTS',
      shortName: 'BTS',
      value: 'BTS',
    },
    {
      fullName: 'Licence générale',
      shortName: 'Licence générale',
      value: 'licence',
    },
    {
      fullName: 'BUT',
      shortName: 'BUT',
      value: 'BUT',
    },
    {
      fullName: 'CPGE',
      shortName: 'CPGE',
      value: 'CPGE',
    },
  ];

  useEffect(() => {
    allowNext(false);
  }, []);

  return (
    <CheckboxGame
      questions={questions}
      getOptions={() => options}
      onFinished={() => allowNext(true)}
      infoSection={
        <Box bg="gray.100" px={6} py={5} borderRadius={4} mb={8}>
          <Text>
            Une formation <b>plus encadrée</b> a un format d'enseignement proche
            du fonctionnement du lycée.
          </Text>
          <Text>
            Une formation <b>moins encadrée</b> a un format d'enseignement qui
            demande plus d'autonomie de ta part car les élèves sont moins guidés
            par les professeurs.
          </Text>
        </Box>
      }
      getQuestionSection={(question) => (
        <Text>
          <i>{question.subject}</i>
        </Text>
      )}
      getSummarySection={(_, questionIndex) =>
        questionIndex !== 0 && (
          <SummaryFrame>
            L’accompagnement de l’enseignant va dépendre en partie de la taille
            de la classe, moins les élèves sont nombreux, plus l’enseignant
            pourra accorder du temps d’accompagnement à chaque élève :
            <UnorderedList>
              <ListItem>
                Dans les formations courtes (CS, FCIL, BTS,...) les classes sont
                souvent comme au lycée (30 élèves)
              </ListItem>
              <ListItem>
                En BUT, les cours varient entre un format de classe comme au
                lycée et des cours en amphithéâtre (avec plus d’élèves)
              </ListItem>
              <ListItem>
                En licence, les cours varient entre des cours magistraux dans
                des amphithéâtres avec beaucoup d’étudiants et des cours en TD
                (travaux dirigés) proche d’un format de classe comme au lycée.
                Il y a souvent moins d’heures de cours, cela demande donc
                beaucoup plus de travail personnel en autonomie.
              </ListItem>
            </UnorderedList>
          </SummaryFrame>
        )
      }
    />
  );
};

export const FormationsAlternance = ({ allowNext }) => {
  const questions = [
    { subject: 'BTS', answerValue: 'alternance' },
    { subject: 'Licence générale', answerValue: 'alternance' },
    { subject: 'CS', answerValue: 'alternance' },
    { subject: 'BUT', answerValue: 'alternance' },
    { subject: 'FCIL', answerValue: 'alternance' },
    {
      subject: 'CPGE',
      answerValue: 'pas-alternance',
      extraInfo:
        'Il existe de l’alternance dans quasiment toutes les filières, sauf les classes préparatoires. Faire une licence générale en alternance c’est encore rare mais possible.',
    },
  ];

  const options = [
    {
      fullName: 'Formations avec alternance possible',
      shortName: 'Alternance possible',
      value: 'alternance',
    },
    {
      fullName: 'Formations sans alternance',
      shortName: "Pas d'alternance",
      value: 'pas-alternance',
    },
  ];

  useEffect(() => {
    allowNext(false);
  }, []);

  return (
    <RadioGame
      questions={questions}
      getOptions={() => options}
      onFinished={() => allowNext(true)}
      introSection={
        <Text textAlign={'center'}>
          Dans cette activité, tu dois dire si les formations peuvent être
          suivies en <b>alternance</b> ou <b>pas</b>.
        </Text>
      }
      getQuestionSection={(question) => (
        <Text>
          <i>
            La formation <b>{question?.subject}</b> peut-elle être suivie en
            alternance ?
          </i>
        </Text>
      )}
      getSummarySection={(_, questionIndex) =>
        questionIndex !== 0 && (
          <SummarySection
            questions={questions}
            options={options}
            questionIndex={questionIndex}
          />
        )
      }
    />
  );
};

export const FormationsParcoursup = ({ allowNext }) => {
  const questions = [
    {
      subject: 'CS',
      answerValue: 'parcoursup',
      extraInfo: `Certaines demandes d’admission aux CS se font directement auprès des établissements.`,
    },
    {
      subject: 'FCIL ou SIL',
      answerValue: 'parcoursup',
      extraInfo: `Une partie des FCIL sont sur Parcoursup. Pour les autres FCIL et les SIL, les inscriptions se font directement auprès des établissements.`,
    },
    { subject: 'CPGE', answerValue: 'parcoursup' },
    { subject: 'BTS', answerValue: 'parcoursup' },
    { subject: 'BUT', answerValue: 'parcoursup' },
    { subject: 'Licence générale', answerValue: 'parcoursup' },
  ];

  const options = [
    {
      fullName: 'Formations avec candidature via Parcoursup',
      shortName: 'Via Parcoursup',
      value: 'parcoursup',
    },
    {
      fullName: 'Formations hors Parcoursup',
      shortName: 'Hors Parcoursup, directement auprès des établissements',
      value: 'non',
    },
  ];

  useEffect(() => {
    allowNext(false);
  }, []);

  return (
    <RadioGame
      isVertical
      questions={questions}
      getOptions={() => options}
      onFinished={() => allowNext(true)}
      introSection={
        <Text textAlign={'center'}>
          Dans cette activité, tu dois dire si les candidatures aux formations
          doivent être faites via <b>Parcoursup</b> ou <b>pas</b>.
        </Text>
      }
      getQuestionSection={(question) => (
        <Text>
          <i>
            Une candidature à la formation <b>{question?.subject}</b>{' '}
            s'effectue...
          </i>
        </Text>
      )}
      getSummarySection={(_, questionIndex) =>
        questionIndex !== 0 && (
          <SummarySection
            questions={questions}
            options={options}
            questionIndex={questionIndex}
          />
        )
      }
    />
  );
};

export const LicencesGenOuPro = ({ allowNext }) => {
  const questions = [
    { subject: 'Elle est plutôt théorique', answerValue: 'generale' },
    {
      subject:
        'Avec des apprentissages plus généralistes, elle a pour objectif de poursuivre en master',
      answerValue: 'generale',
    },
    {
      subject:
        'Avec des enseignements plus pratiques, elle favorise l’insertion professionnelle',
      answerValue: 'professionnelle',
    },
    {
      subject: 'Elle dure 1 an après un Bac +2',
      answerValue: 'professionnelle',
      extraInfo: `Il existe également des licences professionnelles accessibles directement après le bac et qui durent 3 ans.`,
    },
  ];

  const options = [
    {
      fullName: 'Licence générale',
      shortName: 'Licence générale',
      value: 'generale',
    },
    {
      fullName: 'Licence professionnelle',
      shortName: 'Licence professionnelle',
      value: 'professionnelle',
    },
  ];

  useEffect(() => {
    allowNext(false);
  }, []);

  return (
    <RadioGame
      isVertical
      questions={questions}
      getOptions={() => options}
      onFinished={() => {
        allowNext(true);
      }}
      introSection={
        <Text textAlign={'center'}>
          Coche le type de licence qui correspond aux caractéristiques
        </Text>
      }
      getQuestionSection={(question) => (
        <Text fontWeight={'bold'}>
          <i>{question?.subject}</i>
        </Text>
      )}
      getSummarySection={(_, questionIndex) =>
        questionIndex !== 0 && (
          <SummarySection
            questions={questions}
            options={options}
            questionIndex={questionIndex}
          />
        )
      }
    />
  );
};
